import { formatCents } from '@/lib/format';
import SummaryBar from './SummaryBar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface Range {
  min: number;
  max: number;
}

interface BreakdownRow {
  categoryName: string;
  bestimate: Range;
  selectedVendorCount: number;
  categoryId: string;
}

interface BudgetSummaryBarProps {
  bestimate: Range;
  contracted: number;
  paid: number;
  breakdown: BreakdownRow[];
}

function formatRange(r: Range): string {
  if (r.min === r.max) return formatCents(r.min);
  return `${formatCents(r.min)} – ${formatCents(r.max)}`;
}

export default function BudgetSummaryBar({ bestimate, contracted, paid, breakdown }: BudgetSummaryBarProps) {
  return (
    <SummaryBar>
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="space-y-1 text-center rounded-sm cursor-pointer transition-colors hover:[&_p:last-child]:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <p className="label-caps text-xs text-muted-foreground">Bestimate</p>
              <p className="text-lg font-semibold tabular-nums">{formatRange(bestimate)}</p>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-0">
            <div className="border-b p-3">
              <p className="label-caps text-xs text-muted-foreground">Bestimate breakdown</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="px-3 py-2 text-left font-normal">Category</th>
                  <th className="px-3 py-2 text-left font-normal">Source</th>
                  <th className="px-3 py-2 text-right font-normal">Amount</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row) => (
                  <tr key={row.categoryId} className="border-t">
                    <td className="px-3 py-2">{row.categoryName}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {row.selectedVendorCount === 0
                        ? 'Estimate'
                        : `${row.selectedVendorCount} ${row.selectedVendorCount === 1 ? 'vendor' : 'vendors'}`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatRange(row.bestimate)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t font-semibold">
                  <td className="px-3 py-2" colSpan={2}>Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatRange(bestimate)}</td>
                </tr>
              </tfoot>
            </table>
          </PopoverContent>
        </Popover>
        <div className="space-y-1 text-center">
          <p className="label-caps text-xs text-muted-foreground">Contracted</p>
          <p className="text-lg font-semibold tabular-nums">{formatCents(contracted)}</p>
        </div>
        <div className="space-y-1 text-center">
          <p className="label-caps text-xs text-muted-foreground">Paid</p>
          <p className="text-lg font-semibold tabular-nums text-primary">{formatCents(paid)}</p>
        </div>
      </div>
    </SummaryBar>
  );
}
